"use client"
import { useState, useEffect } from "react"
import { useNavigate, useParams } from "react-router-dom"
import axios from "axios"
import { toast } from "react-toastify"
import { baseUrl } from "../../config/baseUrl"
import { PERMISSION_GROUPS } from "../../utils/permissions"

export default function RoleForm() {
  const navigate = useNavigate()
  const { id } = useParams()
  const isEdit = Boolean(id)

  const [formData, setFormData] = useState({
    name: "",
    permissions: [],
  })
  const [loading, setLoading] = useState(false)
  const [fetchLoading, setFetchLoading] = useState(isEdit)

  useEffect(() => {
    if (isEdit) {
      fetchRole()
    }
  }, [id])

  const fetchRole = async () => {
    try {
      setFetchLoading(true)
      const token = localStorage.getItem("token")
      const response = await axios.get(`${baseUrl}/role/role-detail/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })

      if (response.data.status) {
        setFormData({
          name: response.data.data.name,
          permissions: response.data.data.permissions || [],
        })
      }
    } catch (error) {
      console.error("Error fetching role:", error)
      toast.error("Failed to fetch role details")
      navigate("/roles")
    } finally {
      setFetchLoading(false)
    }
  }

  const handlePermissionToggle = (permission) => {
    setFormData((prev) => ({
      ...prev,
      permissions: prev.permissions.includes(permission)
        ? prev.permissions.filter((p) => p !== permission)
        : [...prev.permissions, permission],
    }))
  }

  const handleGroupToggle = (group) => {
    const groupPermissions = group.permissions.map((p) => p.value)
    const allSelected = groupPermissions.every((p) => formData.permissions.includes(p))

    setFormData((prev) => ({
      ...prev,
      permissions: allSelected
        ? prev.permissions.filter((p) => !groupPermissions.includes(p))
        : [...new Set([...prev.permissions, ...groupPermissions])],
    }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!formData.name.trim()) {
      toast.error("Please enter role name")
      return
    }

    if (formData.permissions.length === 0) {
      toast.error("Please select at least one permission")
      return
    }

    try {
      setLoading(true)
      const token = localStorage.getItem("token")
      const url = isEdit ? `${baseUrl}/role/role-update/${id}` : `${baseUrl}/role/role-create`

      const response = await axios({
        method: isEdit ? "PUT" : "POST",
        url,
        data: formData,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })

      if (response.data.status) {
        toast.success(`Role ${isEdit ? "updated" : "created"} successfully`)
        navigate("/roles")
      }
    } catch (error) {
      console.error("Error saving role:", error)
      toast.error(error.response?.data?.message || "Failed to save role")
    } finally {
      setLoading(false)
    }
  }

  if (fetchLoading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading role details...</p>
      </div>
    )
  }

  return (
    <div className="main-sec ">
      <div className="page-header">
        <div>
          <h2 className="page-title">{isEdit ? "Edit Role" : "Create New Role"}</h2>
          <p className="page-subtitle">
            {isEdit ? "Update role details and permissions" : "Define a new role with specific permissions"}
          </p>
        </div>
        <button onClick={() => navigate("/roles")} className="btn-back">
          <i className="fa fa-arrow-left me-2"></i>
          Back to Roles
        </button>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="form-card">
          <div className="form-section">
            <h3 className="section-title">Role Information</h3>
            <div className="form-group">
              <label className="form-label">
                Role Name <span className="required">*</span>
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Enter role name (e.g., Content Manager, Viewer)"
                className="form-input"
                required
              />
            </div>
          </div>

          <div className="form-section">
            <div className="section-header">
              <h3 className="section-title">Permissions</h3>
              <span className="selected-count">
                {formData.permissions.length} permission{formData.permissions.length !== 1 ? "s" : ""} selected
              </span>
            </div>

            <div className="permissions-container">
              {PERMISSION_GROUPS.map((group) => {
                const groupPermissions = group.permissions.map((p) => p.value)
                const selectedCount = groupPermissions.filter((p) => formData.permissions.includes(p)).length
                const allSelected = selectedCount === groupPermissions.length
                const someSelected = selectedCount > 0 && !allSelected

                return (
                  <div key={group.name} className="permission-group">
                    <div className="group-header">
                      <div className="group-title-wrapper">
                        <label className="checkbox-label group-checkbox">
                          <input
                            type="checkbox"
                            checked={allSelected}
                            onChange={() => handleGroupToggle(group)}
                            className="checkbox-input"
                          />
                          <span className={`checkbox-custom ${someSelected ? "indeterminate" : ""}`}>
                            {allSelected && <i className="fa fa-check"></i>}
                            {someSelected && <i className="fa fa-minus"></i>}
                          </span>
                        </label>
                        <div>
                          <h4 className="group-title">{group.name}</h4>
                          <span className="group-count">
                            {selectedCount}/{groupPermissions.length} selected
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="permissions-list">
                      {group.permissions.map((permission) => (
                        <label key={permission.value} className="checkbox-label permission-item">
                          <input
                            type="checkbox"
                            checked={formData.permissions.includes(permission.value)}
                            onChange={() => handlePermissionToggle(permission.value)}
                            className="checkbox-input"
                          />
                          <span className="checkbox-custom">
                            <i className="fa fa-check"></i>
                          </span>
                          <span className="permission-label">{permission.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )
              })}
            </div>
          </div>

          <div className="form-actions">
            <button type="button" onClick={() => navigate("/roles")} className="btn-secondary" disabled={loading}>
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={loading}>
              {loading ? (
                <>
                  <div className="btn-spinner"></div>
                  {isEdit ? "Updating..." : "Creating..."}
                </>
              ) : (
                <>
                  <i className="fa fa-save me-2"></i>
                  {isEdit ? "Update Role" : "Create Role"}
                </>
              )}
            </button>
          </div>
        </div>
      </form>

      <style jsx>{`
        .page-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 32px;
          flex-wrap: wrap;
          gap: 16px;
        }

        .page-title {
          font-size: 28px;
          font-weight: 600;
          color: #1e293b;
          margin: 0;
        }

        .page-subtitle {
          color: #64748b;
          font-size: 14px;
          margin: 4px 0 0 0;
        }

        .btn-back {
          padding: 10px 20px;
          background: white;
          color: #475569;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .btn-back:hover {
          background: #f8fafc;
          border-color: #cbd5e1;
        }

        .form-card {
          background: white;
          border: 1px solid #e2e8f0;
          border-radius: 12px;
          overflow: hidden;
        }

        .form-section {
          padding: 32px;
          border-bottom: 1px solid #e2e8f0;
        }

        .form-section:last-child {
          border-bottom: none;
        }

        .section-title {
          font-size: 18px;
          font-weight: 600;
          color: #1e293b;
          margin: 0 0 20px 0;
        }

        .section-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 24px;
        }

        .selected-count {
          padding: 6px 12px;
          background: #ede9fe;
          color: #6366f1;
          border-radius: 6px;
          font-size: 13px;
          font-weight: 500;
        }

        .form-group {
          margin-bottom: 0;
        }

        .form-label {
          display: block;
          font-weight: 500;
          color: #334155;
          margin-bottom: 8px;
          font-size: 14px;
        }

        .required {
          color: #dc2626;
        }

        .form-input {
          width: 100%;
          padding: 12px 16px;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          font-size: 14px;
          transition: all 0.3s ease;
        }

        .form-input:focus {
          outline: none;
          border-color: #6366f1;
          box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
        }

        .permissions-container {
          display: grid;
          gap: 20px;
        }

        .permission-group {
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          border-radius: 10px;
          overflow: hidden;
        }

        .group-header {
          padding: 16px 20px;
          background: white;
          border-bottom: 1px solid #e2e8f0;
        }

        .group-title-wrapper {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .group-checkbox .checkbox-custom {
          width: 22px;
          height: 22px;
        }

        .group-title {
          font-size: 15px;
          font-weight: 600;
          color: #1e293b;
          margin: 0;
        }

        .group-count {
          font-size: 12px;
          color: #64748b;
        }

        .permissions-list {
          padding: 16px 20px;
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
          gap: 12px;
        }

        .checkbox-label {
          display: flex;
          align-items: center;
          gap: 10px;
          cursor: pointer;
          user-select: none;
        }

        .permission-item {
          padding: 10px 12px;
          background: white;
          border: 1px solid #e2e8f0;
          border-radius: 6px;
          transition: all 0.2s ease;
        }

        .permission-item:hover {
          border-color: #cbd5e1;
          background: #fafbfc;
        }

        .checkbox-input {
          display: none;
        }

        .checkbox-custom {
          width: 18px;
          height: 18px;
          border: 2px solid #cbd5e1;
          border-radius: 4px;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s ease;
          flex-shrink: 0;
          background: white;
        }

        .checkbox-input:checked + .checkbox-custom {
          background: #6366f1;
          border-color: #6366f1;
        }

        .checkbox-custom.indeterminate {
          background: #6366f1;
          border-color: #6366f1;
        }

        .checkbox-custom i {
          color: white;
          font-size: 10px;
          opacity: 0;
          transition: opacity 0.2s ease;
        }

        .checkbox-input:checked + .checkbox-custom i,
        .checkbox-custom.indeterminate i {
          opacity: 1;
        }

        .permission-label {
          font-size: 14px;
          color: #475569;
          font-weight: 450;
        }

        .form-actions {
          padding: 24px 32px;
          background: #f8fafc;
          display: flex;
          justify-content: flex-end;
          gap: 12px;
        }

        .btn-secondary, .btn-primary {
          padding: 10px 24px;
          border-radius: 8px;
          font-weight: 500;
          font-size: 14px;
          cursor: pointer;
          transition: all 0.3s ease;
          border: none;
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .btn-secondary {
          background: white;
          color: #475569;
          border: 1px solid #e2e8f0;
        }

        .btn-secondary:hover:not(:disabled) {
          background: #f8fafc;
          border-color: #cbd5e1;
        }

        .btn-primary {
          background: linear-gradient(135deg, #6366f1 0%, #4f46e5 100%);
          color: white;
          box-shadow: 0 2px 8px rgba(99, 102, 241, 0.2);
        }

        .btn-primary:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(99, 102, 241, 0.3);
        }

        .btn-primary:disabled, .btn-secondary:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .btn-spinner {
          width: 16px;
          height: 16px;
          border: 2px solid rgba(255, 255, 255, 0.3);
          border-top-color: white;
          border-radius: 50%;
          animation: spin 0.6s linear infinite;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        .loading-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 60px 20px;
          color: #64748b;
        }

        .spinner {
          width: 48px;
          height: 48px;
          border: 4px solid #e2e8f0;
          border-top-color: #6366f1;
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
          margin-bottom: 16px;
        }

        @media (max-width: 768px) {
          .page-header {
            flex-direction: column;
            align-items: flex-start;
          }

          .permissions-list {
            grid-template-columns: 1fr;
          }

          .form-actions {
            flex-direction: column-reverse;
          }

          .btn-secondary, .btn-primary {
            width: 100%;
            justify-content: center;
          }
        }
      `}</style>
    </div>
  )
}
